import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, EditIcon, CheckCircleIcon, XCircleIcon, DollarSignIcon, FileTextIcon, CalendarIcon, UserIcon, AlertCircleIcon } from 'lucide-react';
import { Bill } from '../types/Bill';
export function BillDetail() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const bill: Bill = {
    id: id || '1',
    billNumber: 'BILL-2024-001',
    supplierId: '1',
    supplierName: 'Premium Tea Estates Ltd',
    supplierEmail: 'rajesh@premiumtea.lk',
    supplierPhone: '+60 12 345 6789',
    amount: 125000,
    currency: 'LKR',
    status: 'pending_approval',
    dueDate: '2024-02-15',
    issueDate: '2024-01-15',
    description: 'Ceylon Black Tea - January Order',
    items: [{
      id: '1',
      description: 'Ceylon Black Tea - Grade A',
      quantity: 500,
      unitPrice: 200,
      total: 100000
    }, {
      id: '2',
      description: 'Packaging & Handling',
      quantity: 1,
      unitPrice: 25000,
      total: 25000
    }],
    notes: 'Please ensure payment is made before due date to maintain supplier relationship.',
    taxAmount: 0,
    discountAmount: 0,
    subtotal: 125000,
    total: 125000,
    approvalStatus: 'pending',
    financingStatus: 'not_requested'
  };
  const getStatusBadge = (status: Bill['status']) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-800',
      pending_approval: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-blue-100 text-blue-800',
      paid: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800',
      rejected: 'bg-red-100 text-red-800'
    };
    const labels = {
      draft: 'Draft',
      pending_approval: 'Pending Approval',
      approved: 'Approved',
      paid: 'Paid',
      overdue: 'Overdue',
      rejected: 'Rejected'
    };
    return <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${styles[status]}`} data-id="element-4045">
        {labels[status]}
      </span>;
  };
  return <div className="space-y-6" data-id="element-4046">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-4047">
        <button onClick={() => navigate('/bills')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900" data-id="element-4048">
          <ArrowLeftIcon size={20} data-id="element-4049" />
          Back to Bills
        </button>
        <div className="flex items-center gap-3" data-id="element-4050">
          {bill.status === 'draft' && <button onClick={() => navigate(`/bill-form?id=${bill.id}`)} className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-4051">
              <EditIcon size={18} data-id="element-4052" />
              Edit Bill
            </button>}
          {bill.status === 'pending_approval' && <button onClick={() => navigate(`/bill-approval/${bill.id}`)} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700" data-id="element-4053">
              <CheckCircleIcon size={18} data-id="element-4054" />
              Review & Approve
            </button>}
          {bill.status === 'approved' && bill.financingStatus === 'not_requested' && <button onClick={() => navigate(`/bill-financing/${bill.id}`)} className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700" data-id="element-4055">
                <DollarSignIcon size={18} data-id="element-4056" />
                Request Financing
              </button>}
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-4057">
        {/* Left Column - Bill Details */}
        <div className="lg:col-span-2 space-y-6" data-id="element-4058">
          {/* Bill Info Card */}
          <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-4059">
            <div className="flex items-start justify-between mb-6" data-id="element-4060">
              <div data-id="element-4061">
                <h1 className="text-2xl font-bold text-gray-900" data-id="element-4062">
                  {bill.billNumber}
                </h1>
                <p className="text-sm text-gray-500 mt-1" data-id="element-4063">{bill.description}</p>
              </div>
              {getStatusBadge(bill.status)}
            </div>
            {/* Supplier Info */}
            <div className="mb-6 pb-6 border-b border-gray-200" data-id="element-4064">
              <h3 className="text-sm font-medium text-gray-500 mb-3" data-id="element-4065">
                SUPPLIER
              </h3>
              <div className="space-y-2" data-id="element-4066">
                <div className="text-base font-medium text-gray-900" data-id="element-4067">
                  {bill.supplierName}
                </div>
                <div className="text-sm text-gray-600" data-id="element-4068">
                  {bill.supplierEmail}
                </div>
                {bill.supplierPhone && <div className="text-sm text-gray-600" data-id="element-4069">
                    {bill.supplierPhone}
                  </div>}
              </div>
              <button onClick={() => navigate(`/supplier-detail/${bill.supplierId}`)} className="mt-3 text-sm text-blue-600 hover:text-blue-800" data-id="element-4070">
                View Supplier Details →
              </button>
            </div>
            {/* Bill Items */}
            <div className="mb-6" data-id="element-4071">
              <h3 className="text-sm font-medium text-gray-500 mb-3" data-id="element-4072">ITEMS</h3>
              <div className="space-y-3" data-id="element-4073">
                {bill.items.map(item => <div key={item.id} className="flex items-center justify-between py-3 border-b border-gray-100" data-id="element-4074">
                    <div className="flex-1" data-id="element-4075">
                      <div className="text-sm font-medium text-gray-900" data-id="element-4076">
                        {item.description}
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-4077">
                        {item.quantity} × Rs.{' '}
                        {item.unitPrice.toLocaleString('en-LK')}
                      </div>
                    </div>
                    <div className="text-sm font-medium text-gray-900" data-id="element-4078">
                      Rs. {item.total.toLocaleString('en-LK')}
                    </div>
                  </div>)}
              </div>
            </div>
            {/* Totals */}
            <div className="space-y-2" data-id="element-4079">
              <div className="flex justify-between text-sm" data-id="element-4080">
                <span className="text-gray-600" data-id="element-4081">Subtotal:</span>
                <span className="text-gray-900" data-id="element-4082">
                  Rs. {bill.subtotal.toLocaleString('en-LK')}
                </span>
              </div>
              {bill.taxAmount && bill.taxAmount > 0 && <div className="flex justify-between text-sm" data-id="element-4083">
                  <span className="text-gray-600" data-id="element-4084">Tax:</span>
                  <span className="text-gray-900" data-id="element-4085">
                    Rs. {bill.taxAmount.toLocaleString('en-LK')}
                  </span>
                </div>}
              {bill.discountAmount && bill.discountAmount > 0 && <div className="flex justify-between text-sm" data-id="element-4086">
                  <span className="text-gray-600" data-id="element-4087">Discount:</span>
                  <span className="text-red-600" data-id="element-4088">
                    -Rs. {bill.discountAmount.toLocaleString('en-LK')}
                  </span>
                </div>}
              <div className="flex justify-between pt-3 border-t-2 border-gray-200" data-id="element-4089">
                <span className="text-lg font-semibold text-gray-900" data-id="element-4090">
                  Total:
                </span>
                <span className="text-lg font-bold text-gray-900" data-id="element-4091">
                  Rs. {bill.total.toLocaleString('en-LK')}
                </span>
              </div>
            </div>
            {/* Notes */}
            {bill.notes && <div className="mt-6 pt-6 border-t border-gray-200" data-id="element-4092">
                <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-4093">
                  NOTES
                </h3>
                <p className="text-sm text-gray-700" data-id="element-4094">{bill.notes}</p>
              </div>}
          </div>
        </div>
        {/* Right Column - Status & Actions */}
        <div className="space-y-6" data-id="element-4095">
          {/* Dates */}
          <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-4096">
            <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-4097">Dates</h3>
            <div className="space-y-4" data-id="element-4098">
              <div data-id="element-4099">
                <div className="flex items-center text-gray-500 text-sm mb-1" data-id="element-4100">
                  <CalendarIcon size={16} className="mr-2" data-id="element-4101" />
                  Issue Date
                </div>
                <p className="text-sm font-medium text-gray-900" data-id="element-4102">
                  {new Date(bill.issueDate).toLocaleDateString('en-GB')}
                </p>
              </div>
              <div data-id="element-4103">
                <div className="flex items-center text-gray-500 text-sm mb-1" data-id="element-4104">
                  <CalendarIcon size={16} className="mr-2" data-id="element-4105" />
                  Due Date
                </div>
                <p className="text-sm font-medium text-gray-900" data-id="element-4106">
                  {new Date(bill.dueDate).toLocaleDateString('en-GB')}
                </p>
              </div>
              {bill.paidDate && <div data-id="element-4107">
                  <div className="flex items-center text-gray-500 text-sm mb-1" data-id="element-4108">
                    <CalendarIcon size={16} className="mr-2" data-id="element-4109" />
                    Paid Date
                  </div>
                  <p className="text-sm font-medium text-gray-900" data-id="element-4110">
                    {new Date(bill.paidDate).toLocaleDateString('en-GB')}
                  </p>
                </div>}
            </div>
          </div>
          {/* Approval Status */}
          {bill.approvalStatus && <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-4111">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-4112">
                Approval Status
              </h3>
              <div className="space-y-4" data-id="element-4113">
                <div data-id="element-4114">
                  <div className="flex items-center gap-2 mb-2" data-id="element-4115">
                    {bill.approvalStatus === 'pending' && <AlertCircleIcon size={20} className="text-yellow-500" data-id="element-4116" />}
                    {bill.approvalStatus === 'approved' && <CheckCircleIcon size={20} className="text-green-500" data-id="element-4117" />}
                    {bill.approvalStatus === 'rejected' && <XCircleIcon size={20} className="text-red-500" data-id="element-4118" />}
                    <span className="text-sm font-medium text-gray-900" data-id="element-4119">
                      {bill.approvalStatus === 'pending' && 'Pending Approval'}
                      {bill.approvalStatus === 'approved' && 'Approved'}
                      {bill.approvalStatus === 'rejected' && 'Rejected'}
                    </span>
                  </div>
                  {bill.approver && <div data-id="element-4120">
                      <div className="flex items-center text-gray-500 text-sm mb-1" data-id="element-4121">
                        <UserIcon size={16} className="mr-2" data-id="element-4122" />
                        Approver
                      </div>
                      <p className="text-sm font-medium text-gray-900" data-id="element-4123">
                        {bill.approver}
                      </p>
                    </div>}
                  {bill.approvalDate && <div className="mt-2" data-id="element-4124">
                      <div className="flex items-center text-gray-500 text-sm mb-1" data-id="element-4125">
                        <CalendarIcon size={16} className="mr-2" data-id="element-4126" />
                        Approval Date
                      </div>
                      <p className="text-sm font-medium text-gray-900" data-id="element-4127">
                        {new Date(bill.approvalDate).toLocaleDateString('en-GB')}
                      </p>
                    </div>}
                  {bill.approvalNotes && <div className="mt-2" data-id="element-4128">
                      <p className="text-sm text-gray-600" data-id="element-4129">
                        {bill.approvalNotes}
                      </p>
                    </div>}
                </div>
              </div>
            </div>}
          {/* Financing Status */}
          {bill.financingStatus && bill.financingStatus !== 'not_requested' && <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-4130">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-4131">
                Financing Status
              </h3>
              <div className="space-y-4" data-id="element-4132">
                <div data-id="element-4133">
                  <div className="text-sm text-gray-500 mb-1" data-id="element-4134">Status</div>
                  <p className="text-sm font-medium text-gray-900 capitalize" data-id="element-4135">
                    {bill.financingStatus.replace('_', ' ')}
                  </p>
                </div>
                {bill.financingProvider && <div data-id="element-4136">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-4137">Provider</div>
                    <p className="text-sm font-medium text-gray-900" data-id="element-4138">
                      {bill.financingProvider}
                    </p>
                  </div>}
                {bill.financingAmount && <div data-id="element-4139">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-4140">Amount</div>
                    <p className="text-sm font-medium text-gray-900" data-id="element-4141">
                      Rs. {bill.financingAmount.toLocaleString('en-LK')}
                    </p>
                  </div>}
              </div>
            </div>}
        </div>
      </div>
    </div>;
}